import { packChatAuthorName } from "./message-author";
import type { IPackChatMessage } from "./types";

/**
 * AI answers are Markdown, so the raw body carries syntax that must not reach a
 * one-line preview or a search comparison: a row reading `**Final Pay**` and a
 * search for "Final Pay" that misses because of the asterisks are the same bug.
 * Inline syntax only — previews are a single line, so block structure is dropped.
 */
export const packChatPlainText = (body: string) =>
  body
    // Fenced code fences and table pipes carry no meaning on one line.
    .replace(/```+/g, " ")
    .replace(/\|/g, " ")
    // Leading block markers: headings, list bullets, ordered items, quotes.
    .replace(/^\s{0,8}(?:#{1,6}\s+|[-*+]\s+|\d+\.\s+|>\s?)/gm, "")
    // Table divider rows leave nothing readable behind.
    .replace(/^[\s:-]+$/gm, "")
    // Links keep their text, not their target.
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    // Emphasis and inline code markers.
    .replace(/(\*\*|__|`)/g, "")
    .replace(/(^|[\s(])[*_]([^*_\n]+)[*_](?=[\s.,;:!?)]|$)/g, "$1$2")
    .replace(/\s+/g, " ")
    .trim();

/**
 * The one-line summary a row shows for its latest message.
 *
 * An event is history rather than something a person said, so it goes
 * unattributed: an event authored by the technician read as "Taylor: AI could
 * not answer", which says the opposite of what happened.
 */
export const packChatPreview = (message: IPackChatMessage) => ({
  author: message.event_type ? null : packChatAuthorName(message, true),
  body: packChatPlainText(message.body),
});
